/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/components/PulseCard.tsx`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { Activity, ArrowRight, OctagonAlert, TriangleAlert, Info, TrendingUp, Bot } from 'lucide-react';
import { useApi } from '../lib/hooks';
import { useApp } from '../lib/store';
import { useLive } from '../lib/live';
import { navigate } from '../lib/router';
import { I, Badge, Skeleton, ErrorCard } from './ui';
import { ScoreRing, Meter, MiniForecast } from './Charts';
import { chf, date } from '../lib/format';

const SEV_ICON: Record<string, any> = { critical: OctagonAlert, warning: TriangleAlert, info: Info, positive: TrendingUp };

/** Cockpit summary of the Analyse page: score, pillars, 90-day outlook and the top findings. */
export function PulseCard({ tid }: { tid: string }) {
  const app = useApp();
  const live = useLive(tid);
  const { data: p, error, reload } = useApi<any>(`/api/t/${tid}/pulse`, [live]);
  const go = (href: string) => navigate(`/t/${tid}${href}`);
  const act = (a: any) => { if (a.kind === 'ask') { app.setAgentDraft(a.prompt); go('/agent'); } else if (a.kind === 'nav') go(a.href); else go('/insights'); };
  if (error && !p) return <div className="card" style={{ gridColumn: '1 / -1' }}><ErrorCard message={error} onRetry={reload} /></div>;
  const last = p?.forecast.points[p.forecast.points.length - 1];

  return (
    <section className="card pulse" style={{ gridColumn: '1 / -1' }} aria-labelledby="h-pulse">
      <div className="card-head">
        <h3 id="h-pulse">Geschäftsgesundheit</h3>
        <button className="btn ghost sm" onClick={() => go('/insights')}>Analyse <I icon={ArrowRight} sm /></button>
      </div>
      {!p ? <div className="pulse-body"><Skeleton w={104} h={104} /><Skeleton h={104} /><Skeleton h={104} /></div> : (
        <div className="pulse-body">
          <div className="pulse-score">
            <ScoreRing score={p.score} grade={p.grade} status={p.status} size={112} />
          </div>
          <div className="pulse-pillars" data-copy-surface>
            {p.pillars.map((x: any) => (
              <div key={x.key} data-copy-unit title={`${x.label}: ${x.score} von 100`}>
                <span>{x.label}</span><Meter score={x.score} status={x.status} /><b className="num">{x.value}</b>
              </div>
            ))}
          </div>
          <div className="pulse-fc">
            <div className="split" style={{ justifyContent: 'space-between' }}>
              <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>Liquidität nächste 90 Tage</span>
              {p.forecast.alert ? <Badge kind={p.forecast.alert.level === 'critical' ? 'neg' : 'warn'} dot>Engpass möglich</Badge> : <Badge kind="pos" dot>{p.forecast.coverageMonths} Monate Reichweite</Badge>}
            </div>
            <MiniForecast points={p.forecast.points} />
            <div className="pulse-fc-facts" data-copy-surface>
              <span data-copy-unit>Tiefpunkt <b className={`num ${p.forecast.min.expected < 0 ? 'neg' : ''}`}>{chf(p.forecast.min.expected, { currency: false })}</b> am {date(p.forecast.min.date).slice(0, 6)}</span>
              {last && <span data-copy-unit>am {date(last.d).slice(0, 6)} <b className="num">{chf(last.expected, { currency: false })}</b></span>}
            </div>
          </div>
          <div className="pulse-notes">
            {p.insights.length === 0 ? <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>Keine auffälligen Befunde.</p> : p.insights.map((n: any) => (
              <button key={n.id} className="pulse-note" data-sev={n.severity} onClick={() => act(n.action ?? { kind: 'nav', href: '/insights' })}>
                <I icon={SEV_ICON[n.severity] ?? Activity} sm />
                <span><b>{n.title}</b><span>{n.action?.label ?? 'Details'}{n.action?.kind === 'ask' ? <> <I icon={Bot} sm /></> : null}</span></span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
